import { Plus, X, FileText, Calendar, BookOpen, Users, Save, Award, Download } from 'lucide-react';
import LecturerSidebar from '../components/LecturerSidebar';
import '../styles/LecturerTasks.css';
import {  useEffect, useState } from 'react';
import axios from 'axios';
import { parseISO, format } from 'date-fns';
import { pl } from 'date-fns/locale';

export default function LecturerTasks() {
    // State to hold tasks
    const [tasks, setTasks] = useState([]);
    // State to hold loading tasks list
    const [tasksLoading, setTasksLoading] = useState(true);
    // State to hold edit data
    const [editingTask, setEditingTask] = useState(null);
    // State to holde edit modal visibility
    const [editForm, setEditForm] = useState({
        title: "",
        description: "",
        due_date: ""
    });
    // States to add new task
    const [showAddModal, setShowAddModal] = useState(false);
    const [newTask, setNewTask] = useState({
        module_id: "",
        group_code: "",
        title: "",
        description: "",
        due_date: ""
    });
    // State to hold modules for new task
    const [modules, setModules] = useState([]);
    // State to loading modules
    const [modulesLoading, setModulesLoading] = useState(true);
    // State to hold groups for new task
    const [groups, setGroups] = useState([]);
    // State to loading groups
    const [groupsLoading, setGroupsLoading] = useState(true);
    // State to show details modal
    const [showFilesModal, setShowFilesModal] = useState(false);
    const [currentTaskFiles, setCurrentTaskFiles] = useState({ files: [], notGraded: [] });
    // State for grading modal
    const [showGradeModal, setShowGradeModal] = useState(false);
    const [currentTaskForGrading, setCurrentTaskForGrading] = useState(null);
    const [studentsForGrading, setStudentsForGrading] = useState([]);
    const [grades, setGrades] = useState({}); // { student_id: { grade: '', feedback: '' } }
    const [gradingLoading, setGradingLoading] = useState(false);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/tasks/lecturerTasks`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`
            }
        })
        .then(response => {
            setTasks(response.data);
            setTasksLoading(false);
        })
        .catch(error => {
            console.error('Error fetching lecturer tasks:', error);
            setTasksLoading(false);
        });
    }, []);

    useEffect(() => {
       axios.get(`${process.env.REACT_APP_API_URL}/getData/modules`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`
            }
        })
        .then(response => {
            setModules(response.data);
            setModulesLoading(false);
        })
        .catch(error => {
            console.error('Error fetching modules:', error);
            setModulesLoading(false);
        });
    }, []);

    useEffect(() => {
       axios.get(`${process.env.REACT_APP_API_URL}/getData/groups`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`
            }
        })
        .then(response => {
            setGroups(response.data);
            setGroupsLoading(false);
        })
        .catch(error => {
            console.error('Error fetching groups:', error);
            setGroupsLoading(false);
        });
    }, []);

    const handleDelete = async (assignmentId) => {
        if (!window.confirm("Czy na pewno chcesz usunąć to zadanie?")) return;

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/tasks/assignment/${assignmentId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });
            setTasks(tasks.filter(t => t.assignment_id !== assignmentId));
        } catch (error) {
            console.error("Błąd przy usuwaniu zadania:", error);
        }
    };

    // Modal open/close handlers
    const openEditModal = (task) => {
        setEditingTask(task);
        setEditForm({
            title: task.assignment_title,
            description: task.assignment_description || "",
            due_date: task.due_date ? task.due_date.split("T")[0] : ""
        });
    };

    const closeEditModal = () => {
        setEditingTask(null);
        setEditForm({ title: "", description: "", due_date: "" });
    };

    const closeFilesModal = () => {
        setShowFilesModal(false);
        setCurrentTaskFiles({ files: [], notGraded: [] });
    };

    // Open grade modal
    const openGradeModal = async (task) => {
        if (!task.group_assignments_id) {
            alert('Brak informacji o grupie zadania');
            return;
        }

        try {
            setGradingLoading(true);
            setCurrentTaskForGrading(task);
            
            // Pobierz pliki
            const filesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/tasks/assignment/${task.assignment_id}/files`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });

            // Pobierz wszystkich studentów z grupy wraz z ocenami
            const studentsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/lecturer/grades/studentsWithAssignmentGrades?group_assignment_id=${task.group_assignments_id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });

            // Utwórz mapę plików po student_id
            const filesMap = {};
            filesResponse.data.files.forEach(f => {
                filesMap[f.studentId] = f.fileName;
            });

            // Przygotuj dane studentów z plikami i ocenami
            const studentsWithData = studentsResponse.data.map(s => ({
                ...s,
                hasFile: !!filesMap[s.student_id],
                fileName: filesMap[s.student_id] || null
            }));

            // Przygotuj istniejące oceny
            const existingGrades = {};
            studentsWithData.forEach(s => {
                if (s.grade !== null) {
                    existingGrades[s.student_id] = {
                        grade: s.grade.toString(),
                        feedback: s.feedback || ''
                    };
                }
            });

            setStudentsForGrading(studentsWithData);
            setGrades(existingGrades);
            setShowGradeModal(true);
        } catch (err) {
            console.error("Błąd pobierania danych do oceniania:", err);
            alert("Nie udało się pobrać danych do oceniania");
        } finally {
            setGradingLoading(false);
        }
    };

    const closeGradeModal = () => {
        setShowGradeModal(false);
        setCurrentTaskForGrading(null);
        setStudentsForGrading([]);
        setGrades({});
    };

    const handleGradeChange = (studentId, field, value) => {
        setGrades(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [field]: value
            }
        }));
    };

    const saveGrades = async () => {
        if (!currentTaskForGrading?.group_assignments_id) return;

        try {
            setGradingLoading(true);
            const promises = [];

            for (const studentId in grades) {
                const studentGrade = grades[studentId];
                if (studentGrade.grade && studentGrade.grade.trim() !== '') {
                    promises.push(
                        axios.post(
                            `${process.env.REACT_APP_API_URL}/lecturer/grades/addAssignment`,
                            {
                                student_id: parseInt(studentId),
                                group_assignment_id: currentTaskForGrading.group_assignments_id,
                                grade: parseFloat(studentGrade.grade),
                                feedback: studentGrade.feedback || null
                            },
                            {
                                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                            }
                        )
                    );
                }
            }

            await Promise.all(promises);
            
            // Odśwież listę zadań
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/tasks/lecturerTasks`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`
                }
            });
            setTasks(response.data);

            alert('Oceny zostały zapisane');
            closeGradeModal();
        } catch (err) {
            console.error("Błąd zapisywania ocen:", err);
            alert("Nie udało się zapisać ocen");
        } finally {
            setGradingLoading(false);
        }
    };

    // Save edited task handler
    const saveEdit = async () => {
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/tasks/assignment/${editingTask.assignment_id}`,
                editForm,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                }
            );

            setTasks(tasks.map(t =>
                t.assignment_id === editingTask.assignment_id
                    ? { ...t, assignment_title: editForm.title, assignment_description: editForm.description, due_date: editForm.due_date }
                    : t
            ));

            closeEditModal();
        } catch (error) {
            console.error("Błąd przy edytowaniu zadania:", error);
        }
    };

    // Add new task handlers
    const openAddModal = () => setShowAddModal(true);
    const closeAddModal = () => setShowAddModal(false);

    const saveNewTask = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/tasks/assignment`,
                newTask,
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
            );

            setTasks([{ 
                assignment_id: response.data.assignment_id,
                assignment_title: newTask.title,
                assignment_description: newTask.description,
                due_date: newTask.due_date,
                module_name: newTask.module_id,
                group_name: newTask.group_code,
                graded_count: 0,
                not_submitted: 0
            }, ...tasks]);

            closeAddModal();
        } catch (error) {
            console.error("Błąd przy dodawaniu zadania:", error);
        }
    };

    // Details modal handlers
    const openFilesModal = async (task) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/tasks/assignment/${task.assignment_id}/files`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });
            setCurrentTaskFiles(response.data);
            setShowFilesModal(true);
        } catch (err) {
            console.error("Błąd pobierania plików:", err);
            alert("Nie udało się pobrać plików");
        }
    };

    // Download file handler
    const downloadFile = async (fileName) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/tasks/files/${encodeURIComponent(fileName)}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Błąd pobierania pliku:", err);
        }
    };

    return (
        <div className='lecture-dashboard-panel'>
            {/* Sidebar */}
            <LecturerSidebar />
            {/* Main Content */}
            <div className='main-content'>
                {/* Header */}
                <div className='header'>
                    <h1 className='header-title'>Zadania</h1>
                </div>
                <div className='dashboard'>
                    <h2 className='page-title'>Zadania</h2>
                    <p className='page-subtitle'>Zarządzaj zadaniami kursantów</p>
                    {/* Main dashboard content */}
                    <div className='lecturer-tasks-actions'>
                        <button 
                            className='btn btn-primary' 
                            style={{marginBottom: 16}}
                             onClick={openAddModal}
                        >
                            <Plus size={16} style={{ marginRight: 8 }} />
                            Dodaj nowe zadanie
                        </button>
                    </div>
                    {/* Tasks List */}
                    {tasksLoading ? (
                        <p>Ładowanie zadań...</p>
                    ) : tasks.length === 0 ? (
                        <p>Brak zadań do wyświetlenia.</p>
                    ) : (
                        <div className='lecturer-tasks-grid'>
                            {tasks.map(task => (
                                <div key={task.assignment_id} className='lecturer-task-card' onClick={() => openFilesModal(task)}>
                                    <h2 className='lecturer-task-title'>{task.assignment_title}</h2>
                                    <p className="lecturer-task-desc">{task.assignment_description || "Brak opisu"}</p>
                                    <p className="lecturer-task-info"><strong>Przedmiot:</strong> {task.module_name}</p>
                                    <p className="lecturer-task-info"><strong>Grupa:</strong> {task.group_name}</p>
                                    <p className="lecturer-task-info"><strong>Termin:</strong> {task.due_date ? format(parseISO(task.due_date), 'dd.MM.yyyy', { locale: pl }) : 'Brak terminu'}</p>
                                    <div className="lecturer-task-stats">
                                        <p className="lecturer-task-not-graded">Nieocenione: {task.not_submitted}</p>
                                        <p className="lecturer-task-graded">Ocenione: {task.graded_count}</p>
                                    </div>
                                    <div className="lecturer-task-actions" onClick={(e) => e.stopPropagation()}>
                                        <button className='task-button lecturer-task-grade' onClick={() => openGradeModal(task)}>
                                            <Award size={14} style={{ marginRight: 4 }} />
                                            Oceń
                                        </button>
                                        <button className='task-button lecturer-task-edit' onClick={() => openEditModal(task)}>Edytuj</button>
                                        <button className='task-button lecturer-task-delete' onClick={() => handleDelete(task.assignment_id)}>Usuń</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {/* Edit Modal */}
            {editingTask && (
                <div className="task-modal-overlay" onClick={closeEditModal}>
                    <div className="task-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="task-modal-header">
                            <h2>
                                <FileText size={20} style={{ marginRight: 8 }} />
                                Edytuj zadanie
                            </h2>
                            <button className="task-modal-close" onClick={closeEditModal} aria-label="Zamknij">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="task-modal-body">
                            <div className="task-form-field">
                                <label>
                                    <FileText size={16} style={{ marginRight: 6 }} />
                                    Tytuł
                                </label>
                                <input
                                    type="text"
                                    value={editForm.title}
                                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                    placeholder="Wprowadź tytuł zadania"
                                />
                            </div>
                            <div className="task-form-field">
                                <label>
                                    <FileText size={16} style={{ marginRight: 6 }} />
                                    Opis
                                </label>
                                <textarea
                                    value={editForm.description}
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    placeholder="Wprowadź opis zadania"
                                    rows={4}
                                />
                            </div>
                            <div className="task-form-field">
                                <label>
                                    <Calendar size={16} style={{ marginRight: 6 }} />
                                    Termin
                                </label>
                                <input
                                    type="date"
                                    value={editForm.due_date}
                                    onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="task-modal-actions">
                            <button className="task-edit-btn btn-edit-task-secondary" onClick={closeEditModal}>Anuluj</button>
                            <button className="task-edit-btn btn-edit-task-primary" onClick={saveEdit}>
                                <Save size={16} style={{ marginRight: 6 }} />
                                Zapisz
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Add Modal */}
            {showAddModal && (
                <div className="task-modal-overlay" onClick={closeAddModal}>
                    <div className="task-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="task-modal-header">
                            <h2>
                                <Plus size={20} style={{ marginRight: 8 }} />
                                Dodaj nowe zadanie
                            </h2>
                            <button className="task-modal-close" onClick={closeAddModal} aria-label="Zamknij">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="task-modal-body">
                            <div className="task-form-field">
                                <label>
                                    <FileText size={16} style={{ marginRight: 6 }} />
                                    Tytuł
                                </label>
                                <input
                                    type="text"
                                    value={newTask.title}
                                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                                    placeholder="Wprowadź tytuł zadania"
                                />
                            </div>
                            <div className="task-form-field">
                                <label>
                                    <FileText size={16} style={{ marginRight: 6 }} />
                                    Opis
                                </label>
                                <textarea
                                    value={newTask.description}
                                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                                    placeholder="Wprowadź opis zadania"
                                    rows={4}
                                />
                            </div>
                            <div className="task-form-field">
                                <label>
                                    <Calendar size={16} style={{ marginRight: 6 }} />
                                    Termin
                                </label>
                                <input
                                    type="date"
                                    value={newTask.due_date}
                                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                                />
                            </div>
                            <div className="task-form-field">
                                <label>
                                    <BookOpen size={16} style={{ marginRight: 6 }} />
                                    Przedmiot
                                </label>
                                {modulesLoading ? (
                                    <p>Ładowanie przedmiotów...</p>
                                ) : (
                                    <select
                                        value={newTask.module_id}
                                        onChange={(e) => setNewTask({ ...newTask, module_id: e.target.value })}
                                    >
                                        <option value="">Wybierz przedmiot</option>
                                        {modules.map(mod => (
                                            <option key={mod.module_id} value={mod.module_id}>
                                                {mod.module_name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="task-form-field">
                                <label>
                                    <Users size={16} style={{ marginRight: 6 }} />
                                    Grupa
                                </label>
                                {groupsLoading ? (
                                    <p>Ładowanie grup...</p>
                                ) : (
                                    <select
                                        value={newTask.group_code}
                                        onChange={(e) => setNewTask({ ...newTask, group_code: e.target.value })}
                                    >
                                        <option value="">Wybierz grupę</option>
                                        {groups.map(grp => (
                                            <option key={grp.group_code} value={grp.group_code}>
                                                {grp.group_name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                        <div className="task-modal-actions">
                            <button className="task-edit-btn btn-edit-task-secondary" onClick={closeAddModal}>Anuluj</button>
                            <button className="task-edit-btn btn-edit-task-primary" onClick={saveNewTask}>
                                <Save size={16} style={{ marginRight: 6 }} />
                                Zapisz
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Files Modal */}
            {showFilesModal && (
                <div className="task-modal-overlay" onClick={closeFilesModal}>
                    <div className="task-modal-content task-modal-content-large" onClick={(e) => e.stopPropagation()}>
                        <div className="task-modal-header">
                            <h2>
                                <FileText size={20} style={{ marginRight: 8 }} />
                                Pliki i nieocenieni studenci
                            </h2>
                            <button className="task-modal-close" onClick={closeFilesModal} aria-label="Zamknij">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="task-modal-body">
                            <div className="task-files-section">
                                <h3>
                                    <Users size={18} style={{ marginRight: 8 }} />
                                    Nieocenieni studenci
                                </h3>
                                {currentTaskFiles.notGraded.length === 0 ? (
                                    <p className="task-empty-message">Brak nieocenionych studentów</p>
                                ) : (
                                    <ul className="task-student-list">
                                        {currentTaskFiles.notGraded.map(s => (
                                            <li key={s.student_id} className="task-student-item">
                                                <span className="task-student-name">{s.first_name} {s.last_name}</span>
                                                <span className="task-student-email">{s.email}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="task-files-section">
                                <h3>
                                    <FileText size={18} style={{ marginRight: 8 }} />
                                    Pliki przesłane przez studentów
                                </h3>
                                {currentTaskFiles.files.length === 0 ? (
                                    <p className="task-empty-message">Brak przesłanych plików</p>
                                ) : (
                                    <ul className="task-files-list">
                                        {currentTaskFiles.files.map(f => (
                                            <li key={f.studentId} className="task-file-item">
                                                <span className="task-file-student">{f.studentName}</span>
                                                <button 
                                                    className="task-file-download-btn" 
                                                    onClick={() => downloadFile(f.fileName)}
                                                >
                                                    <FileText size={14} style={{ marginRight: 6 }} />
                                                    {f.fileName}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                        <div className="task-modal-actions">
                            <button className="task-edit-btn btn-edit-task-secondary" onClick={closeFilesModal}>Zamknij</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Grade Modal */}
            {showGradeModal && currentTaskForGrading && (
                <div className="task-modal-overlay" onClick={closeGradeModal}>
                    <div className="task-modal-content task-modal-content-large" onClick={(e) => e.stopPropagation()}>
                        <div className="task-modal-header">
                            <h2>
                                <Award size={20} style={{ marginRight: 8 }} />
                                Wystaw oceny - {currentTaskForGrading.assignment_title}
                            </h2>
                            <button className="task-modal-close" onClick={closeGradeModal} aria-label="Zamknij">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="task-modal-body">
                            {gradingLoading ? (
                                <p style={{ textAlign: 'center' }}>Ładowanie danych...</p>
                            ) : studentsForGrading.length === 0 ? (
                                <p className="task-empty-message">Brak studentów do ocenienia</p>
                            ) : (
                                <div className="task-grade-list">
                                    {studentsForGrading.map(student => (
                                        <div key={student.student_id} className="task-grade-item">
                                            <div className="task-grade-student-info">
                                                <div className="task-grade-student-name">
                                                    {student.first_name} {student.last_name}
                                                </div>
                                                {student.hasFile ? (
                                                    <div className="task-grade-file-info">
                                                        <div className="task-grade-file-badge">
                                                            <FileText size={12} style={{ marginRight: 4 }} />
                                                            {student.fileName}
                                                        </div>
                                                        <button
                                                            className="task-grade-download-btn"
                                                            onClick={() => downloadFile(student.fileName)}
                                                            title="Pobierz plik"
                                                        >
                                                            <Download size={14} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="task-grade-no-file-badge">
                                                        Brak pliku
                                                    </div>
                                                )}
                                            </div>
                                            <div className="task-grade-inputs">
                                                <div className="task-grade-input-group">
                                                    <label>Ocena</label>
                                                    <input
                                                        type="number"
                                                        min="2"
                                                        max="5"
                                                        step="0.5"
                                                        value={grades[student.student_id]?.grade || ''}
                                                        onChange={(e) => handleGradeChange(student.student_id, 'grade', e.target.value)}
                                                        placeholder="2.0 - 5.0"
                                                    />
                                                </div>
                                                <div className="task-grade-input-group task-grade-feedback">
                                                    <label>Uwagi</label>
                                                    <textarea
                                                        value={grades[student.student_id]?.feedback || ''}
                                                        onChange={(e) => handleGradeChange(student.student_id, 'feedback', e.target.value)}
                                                        placeholder="Opcjonalne uwagi..."
                                                        rows={2}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="task-modal-actions">
                            <button className="task-edit-btn btn-edit-task-secondary" onClick={closeGradeModal}>Anuluj</button>
                            <button 
                                className="task-edit-btn btn-edit-task-primary" 
                                onClick={saveGrades}
                                disabled={gradingLoading}
                            >
                                <Save size={16} style={{ marginRight: 6 }} />
                                {gradingLoading ? 'Zapisywanie...' : 'Zapisz oceny'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
    );
}
